import { useState, useCallback } from 'react';
import './App.css';

const FORMSPREE_URL = 'https://formspree.io/f/mbdpolzq';

const RADIO_OPTIONS = [
  { value: 'not-close', label: 'Not even close' },
  { value: 'almost', label: 'Almost' },
  { value: 'yes-more', label: 'Yes, but I want more' },
];

function PhoneMockup({ src, alt, className = '' }) {
  return (
    <div className={`phone-mockup ${className}`}>
      <div className="phone-frame">
        <div className="phone-notch" />
        <img src={`${process.env.PUBLIC_URL}/${src}`} alt={alt} className="phone-screen" />
      </div>
    </div>
  );
}

function WaitlistForm({ id }) {
  const [email, setEmail] = useState('');
  const [pullupStatus, setPullupStatus] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const canSubmit = email.trim() !== '' && pullupStatus !== '';

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!email.trim() || !pullupStatus) return;
      setSubmitting(true);
      setError(false);

      try {
        const res = await fetch(FORMSPREE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            email,
            pullup_status: pullupStatus || 'not answered',
            submitted_at: new Date().toISOString(),
          }),
        });
        if (res.ok) {
          setSubmitted(true);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setSubmitting(false);
      }
    },
    [email, pullupStatus],
  );

  if (submitted) {
    return (
      <div className="form-success" role="status">
        <p className="success-text">You're on the list.</p>
        <p className="success-subtext">We'll be in touch when it's time.</p>
      </div>
    );
  }

  return (
    <form className="waitlist-form" onSubmit={handleSubmit} noValidate>
      <label htmlFor={`email-${id}`} className="sr-only">
        Email address
      </label>
      <input
        id={`email-${id}`}
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="Your email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="email-input"
      />

      <fieldset className="radio-group">
        <legend className="radio-legend">Can you currently do a pull-up?</legend>
        <div className="radio-options" role="radiogroup">
          {RADIO_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`radio-option${pullupStatus === opt.value ? ' selected' : ''}`}
            >
              <input
                type="radio"
                name={`pullup-${id}`}
                value={opt.value}
                checked={pullupStatus === opt.value}
                onChange={() => setPullupStatus(opt.value)}
                className="sr-only"
              />
              <span className="radio-dot" aria-hidden="true" />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      <button type="submit" className="submit-btn" disabled={submitting || !canSubmit}>
        {submitting ? 'Joining\u2026' : 'Get early access'}
      </button>

      {error && (
        <p className="error-msg" role="alert">
          Something went wrong. Please try again.
        </p>
      )}
    </form>
  );
}

function App() {
  return (
    <main>
      <header className="top-bar">
        <span className="brand-name">Unassisted</span>
      </header>

      <section className="hero" aria-label="Join the waitlist">
        <div className="hero-inner">
          <div className="hero-phones">
            <PhoneMockup
              src="onboardingscreenshot.png"
              alt="Unassisted onboarding screen"
              className="phone-back"
            />
            <PhoneMockup
              src="homescreenshot.png"
              alt="Unassisted home screen showing progress tracking"
              className="phone-front"
            />
          </div>

          <div className="hero-content">
            <p className="hero-tagline">Pull-Up Training App</p>

            <h1 className="hero-headline">
              Your first pull-up
              <br />
              <span className="headline-accent">in 8 weeks.</span>
            </h1>

            <p className="hero-sub">
              Unassisted is the app that holds you accountable
              — taking you from zero to your first rep.
            </p>

            <WaitlistForm id="hero" />

            <p className="fine-print">
              Free to join. We'll notify you when the app launches.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

export default App;
